// ============================================================================
// SignageAddressUpsert.tsx — PARENT container (single file for Add/Edit)
// - Detects add vs edit by presence of :addressId route param
// - Fetches existing address for edit, maps to child form
// - Submits to POST /addresses (add) or PUT /addresses/:id (edit)
// - Keeps all Formik + validation + API here
// - Renders the pure-UI <AddressForm /> child from the same folder
// ============================================================================

import React, { useEffect, useMemo, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import AddressForm, { AddressValues } from "components/AddEditAddresses";
import { showError, showSuccess } from "toast/toast";
import {
  addressLine1Validation,
  cityValidation,
  countryValidation,
  firstNameValidation,
  lastNameValidation,
  phoneValidation,
  stateValidation,
  zipcodeValidation,
} from "validationSchemas/common";
import { getAuthCookie } from "../../utils/authCookies";


export default function AgentAddEditAddresses() {
  const navigate = useNavigate();
  const { addressId } = useParams<{ addressId?: string }>();
  const token = getAuthCookie("sessionToken") || "";
  const userId = getAuthCookie("userId") || "";
  const apiBase = process.env.REACT_APP_API_URL as string;
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string;

  const [loading, setLoading] = useState(!!addressId);
  const [initial, setInitial] = useState<Partial<AddressValues>>({});
  const [defaultAddressId, setDefaultAddressId] = useState<string | null>(null);

  // Fetch default address ID
  useEffect(() => {
    if (!userId || !token) return;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/addresses/default/${userId}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const result = await res.json();
        if (res.ok && result?._id) {
          setDefaultAddressId(result._id);
        }
      } catch (e) {
        console.error("Error fetching default address:", e);
      }
    })();
  }, [userId, apiBase, token]);

  // Fetch existing address for edit
  useEffect(() => {
    if (!addressId) return;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${apiBase}/addresses/${addressId}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const result = await res.json();
        if (!res.ok || result?.error) {
          const message = result?.error || result?.message || "Error fetching address";
          showError(message);
          throw new Error(message);
        }
        setInitial({
          label_of_address: result?.label || "",
          firstName: result?.first_name || "",
          lastName: result?.last_name || "",
          company: result?.company || "",
          addressLine1: result?.address_details?.addressLine1 || "",
          addressLine2: result?.address_details?.addressLine2 || "",
          city: result?.address_details?.city || "",
          state: result?.address_details?.state || "",
          zipcode: result?.address_details?.zip || "",
          country: result?.address_details?.country || "",
          phone: result?.address_details?.phone || "",
          latitude: result?.address_details?.latitude || 0,
          longitude: result?.address_details?.longitude || 0,
          cityCode: result?.address_details?.cityCode || "",
          stateCode: result?.address_details?.stateCode || "",
          countryCode: result?.address_details?.countryCode || "",
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [addressId, apiBase, token]);

  const initialValues: AddressValues = useMemo(
    () => ({
      label_of_address: initial.label_of_address || "",
      firstName: initial.firstName || "",
      lastName: initial.lastName || "",
      company: initial.company || "",
      addressLine1: initial.addressLine1 || "",
      addressLine2: initial.addressLine2 || "",
      city: initial.city || "",
      state: initial.state || "",
      zipcode: initial.zipcode || "",
      country: initial.country || "",
      phone: initial.phone || "",
      cityCode: initial.cityCode || "",
      stateCode: initial.stateCode || "",
      countryCode: initial.countryCode || "",
      latitude: typeof initial.latitude === "number" ? initial.latitude : 0,
      longitude: typeof initial.longitude === "number" ? initial.longitude : 0,
    }),
    [initial]
  );
  

  const schema = Yup.object({
    // label_of_address: Yup.string().required("Label of address is required"),
    // firstName: firstNameValidation,
    // lastName: lastNameValidation,
    addressLine1: addressLine1Validation,
    city: cityValidation,
    state: stateValidation,
    zipcode: zipcodeValidation,
    country: countryValidation,
    // phone: phoneValidation,
  });

  if (loading) {
    return <div className="d-flex justify-content-center py-5">Loading...</div>;
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={async (values, helpers) => {
        try {
          const isEdit = !!addressId;
          const url = isEdit ? `${apiBase}/addresses/${addressId}` : `${apiBase}/addresses`;
          const method = isEdit ? "PUT" : "POST";
          const body = isEdit
            ? JSON.stringify({
              //  user_id: userId,
              label: values.label_of_address,
              first_name: values.firstName,
              last_name: values.lastName,
              company: values.company,
              address_details: {
                label: values.label_of_address,
                addressLine1: values.addressLine1,
                addressLine2: values.addressLine2,
                city: values.city,
                state: values.state,
                zip: values.zipcode,
                country: values.country,
                // phone: values.phone,
                phone: (values.phone ?? "").toString().trim() || "0000000000",
                cityCode: values.cityCode,
                stateCode: values.stateCode,
                countryCode: values.countryCode,
                latitude: values.latitude,
                longitude: values.longitude,
              },
            })
            : JSON.stringify({
              user_id: userId,
              label: values.label_of_address,
              first_name: values.firstName,
              last_name: values.lastName,
              company: values.company,
              address_details: {
                addressLine1: values.addressLine1,
                addressLine2: values.addressLine2,
                label: values.label_of_address,
                city: values.city,
                state: values.state,
                zip: values.zipcode,
                country: values.country,
                // phone: values.phone,
                phone: (values.phone ?? "").toString().trim() || "0000000000",
                cityCode: values.cityCode,
                stateCode: values.stateCode,
                countryCode: values.countryCode,
                latitude: values.latitude,
                longitude: values.longitude,
              },
            });

          const res = await fetch(url, {
            method,
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body,
          });
          const result = await res.json().catch(() => ({}));
          if (!res.ok || result?.error) {
            const message = result?.error || result?.message || `Failed to ${addressId ? "update" : "add"} address`;
            showError(message);
            throw new Error(message);
          }

          // If editing default address, also update user profile
          if (isEdit && addressId && defaultAddressId && addressId === defaultAddressId) {
            try {
              const userUpdateBody = JSON.stringify({
                firmDetails: {
                  address: {
                    addressLine1: values.addressLine1,
                    addressLine2: values.addressLine2 || "",
                    city: values.city,
                    cityCode: values.cityCode || "",
                    state: values.state,
                    stateCode: values.stateCode || "",
                    country: values.country,
                    countryCode: values.countryCode || "",
                    zip: values.zipcode,
                    phone: (values.phone ?? "").toString().trim() || "0000000000",
                    latitude: values.latitude || 0,
                    longitude: values.longitude || 0,
                  },
                },
              });

              const userRes = await fetch(`${apiBase}/users/${userId}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: userUpdateBody,
              });

              const userResult = await userRes.json().catch(() => ({}));
              if (!userRes.ok || userResult?.error) {
                console.warn("Address updated but user profile update failed:", userResult?.error || userResult?.message);
              }
            } catch (userErr) {
              console.error("Error updating user profile:", userErr);
              // Don't throw - address update was successful
            }
          }

          showSuccess(addressId ? "Address updated successfully!" : "Address added successfully!");
          navigate("/agent/addresses");
        } catch (err: any) {
          console.error(err);
          showError(err?.message || "Something went wrong");
        } finally {
          helpers.setSubmitting(false);
        }
      }}
    >
      {(formik) => (
        <div className="content">
          <div className="col-md-12 d-flex justify-content-center mt-4">
            <div className="col-md-8 br-8">
              <h3 className="pt-1  roboto-new fs-22" style={{ color: "#101828" }}>{addressId ? "Edit Address" : "Add Address"}</h3>
              <p className="roboto-new-Paragraph fs-17">{addressId ? "Update address entry for your records" : "Create a new address entry for your records"}</p>
              <div className="card-body shadow-lg mb-5" style={{ borderRadius: "17px" }}>
                <div className="p-2 px-4 mt-1">
                  <AddressForm
                    formik={formik as any}
                    googleMapsApiKey={googleMapsApiKey}
                    onCancel={() => navigate(-1)}
                    submitLabel={addressId ? "Save" : "Add Address"}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Formik>
  );
}

